import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Scale, Search, Plus, Eye, Save, X } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const BradenScalePage = () => {
  const [patients, setPatients] = useState([]);
  const [scales, setScales] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAssessment, setShowAssessment] = useState(false);
  const [scores, setScores] = useState({
    sensoryPerception: 3, moisture: 3, activity: 3, mobility: 3, nutrition: 3, frictionShear: 2
  });
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchPatients();
    fetchScales();
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      if (response.data.success) {
        setPatients(response.data.patients);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchScales = async () => {
    try {
      const response = await api.get('/braden-scale');
      if (response.data.success) {
        setScales(response.data.scales);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }
    try {
      await api.post('/braden-scale', {
        patientId: selectedPatient.id,
        patientName: `${selectedPatient.firstName} ${selectedPatient.lastName}`,
        ...scores,
        notes
      });
      toast.success('Braden Scale assessment saved');
      fetchScales();
      resetForm();
    } catch (error) {
      toast.error('Error saving assessment');
    }
  };

  const resetForm = () => {
    setShowAssessment(false);
    setSelectedPatient(null);
    setScores({ sensoryPerception: 3, moisture: 3, activity: 3, mobility: 3, nutrition: 3, frictionShear: 2 });
    setNotes('');
  };

  const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);
  const riskLevel = totalScore <= 9 ? 'Very High Risk' : totalScore <= 12 ? 'High Risk' : totalScore <= 14 ? 'Moderate Risk' : totalScore <= 18 ? 'Mild Risk' : 'No Risk';
  const riskColor = totalScore <= 9 ? 'text-red-600 bg-red-100' : totalScore <= 12 ? 'text-orange-600 bg-orange-100' : totalScore <= 14 ? 'text-yellow-600 bg-yellow-100' : 'text-green-600 bg-green-100';

  const categories = [
    { key: 'sensoryPerception', label: 'Sensory Perception', options: [{ value: 1, label: 'Completely Limited' }, { value: 2, label: 'Very Limited' }, { value: 3, label: 'Slightly Limited' }, { value: 4, label: 'No Impairment' }]},
    { key: 'moisture', label: 'Moisture', options: [{ value: 1, label: 'Constantly Moist' }, { value: 2, label: 'Very Moist' }, { value: 3, label: 'Occasionally Moist' }, { value: 4, label: 'Rarely Moist' }]},
    { key: 'activity', label: 'Activity', options: [{ value: 1, label: 'Bedfast' }, { value: 2, label: 'Chairfast' }, { value: 3, label: 'Walks Occasionally' }, { value: 4, label: 'Walks Frequently' }]},
    { key: 'mobility', label: 'Mobility', options: [{ value: 1, label: 'Completely Immobile' }, { value: 2, label: 'Very Limited' }, { value: 3, label: 'Slightly Limited' }, { value: 4, label: 'No Limitation' }]},
    { key: 'nutrition', label: 'Nutrition', options: [{ value: 1, label: 'Very Poor' }, { value: 2, label: 'Probably Inadequate' }, { value: 3, label: 'Adequate' }, { value: 4, label: 'Excellent' }]},
    { key: 'frictionShear', label: 'Friction & Shear', options: [{ value: 1, label: 'Problem' }, { value: 2, label: 'Potential Problem' }, { value: 3, label: 'No Apparent Problem' }]}
  ];

  const filteredPatients = patients.filter(patient =>
    `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get latest scale for each patient
  const patientLatestScale = {};
  scales.forEach(s => {
    if (!patientLatestScale[s.patientId] || new Date(s.createdAt) > new Date(patientLatestScale[s.patientId].createdAt)) {
      patientLatestScale[s.patientId] = s;
    }
  });

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Scale className="w-6 h-6 text-[#f39c12]" />
                Braden Scale Assessment
              </h1>
              <p className="text-gray-500 text-sm mt-1">Assess pressure ulcer risk using the Braden Scale</p>
            </div>
            <Button onClick={() => setShowAssessment(true)} className="bg-[#f39c12] hover:bg-[#e67e22] text-white">
              <Plus className="w-4 h-4 mr-2" />New Assessment
            </Button>
          </div>
        </div>

        {showAssessment ? (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">New Braden Scale Assessment</h2>
              <Button variant="ghost" onClick={resetForm}><X className="w-5 h-5" /></Button>
            </div>

            {!selectedPatient ? (
              <div>
                <Label>Select Patient</Label>
                <div className="grid grid-cols-3 gap-3 mt-2 max-h-60 overflow-y-auto">
                  {patients.map(patient => (
                    <button key={patient.id} onClick={() => setSelectedPatient(patient)}
                      className="p-3 border rounded-lg text-left hover:bg-gray-50 flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#3498db] rounded-full flex items-center justify-center text-white text-sm">
                        {patient.firstName?.[0]}{patient.lastName?.[0]}
                      </div>
                      <span className="font-medium text-sm">{patient.firstName} {patient.lastName}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#3498db] rounded-full flex items-center justify-center text-white font-bold">
                      {selectedPatient.firstName?.[0]}{selectedPatient.lastName?.[0]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{selectedPatient.firstName} {selectedPatient.lastName}</h3>
                      <p className="text-sm text-gray-500">Braden Scale Assessment</p>
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-lg ${riskColor}`}>
                    <div className="text-sm font-medium">Total Score: {totalScore}</div>
                    <div className="text-xs">{riskLevel}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  {categories.map((cat) => (
                    <div key={cat.key} className="p-4 border rounded-lg">
                      <Label className="font-medium mb-2 block">{cat.label}</Label>
                      <select value={scores[cat.key]} onChange={(e) => setScores({...scores, [cat.key]: parseInt(e.target.value)})} className="w-full p-2 border rounded">
                        {cat.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.value} - {opt.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                <div className="mb-6">
                  <Label>Notes</Label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full mt-1 p-3 border rounded" rows={3} placeholder="Additional notes..." />
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button className="bg-[#f39c12] hover:bg-[#e67e22] text-white" onClick={handleSubmit}>
                    <Save className="w-4 h-4 mr-2" />Save Assessment
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search patients..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Last Assessment</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Risk Level</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
                  ) : filteredPatients.map((patient) => {
                    const latestScale = patientLatestScale[patient.id];
                    return (
                      <tr key={patient.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#3498db] rounded-full flex items-center justify-center text-white font-medium">
                              {patient.firstName?.[0]}{patient.lastName?.[0]}
                            </div>
                            <span className="font-medium">{patient.firstName} {patient.lastName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">{latestScale ? new Date(latestScale.createdAt).toLocaleDateString() : 'No assessment'}</td>
                        <td className="px-6 py-4 font-bold">{latestScale?.totalScore || '-'}</td>
                        <td className="px-6 py-4">
                          {latestScale ? (
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              latestScale.totalScore <= 9 ? 'bg-red-100 text-red-800' :
                              latestScale.totalScore <= 12 ? 'bg-orange-100 text-orange-800' :
                              latestScale.totalScore <= 14 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>{latestScale.riskLevel}</span>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {latestScale && <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>}
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedPatient(patient); setShowAssessment(true); }}>
                              <Scale className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BradenScalePage;
